/**
 * PartnerSummaryTab — ported from fleet-partner's Dashboard.tsx (overview/summary).
 *
 * Shows 8 stat cards and 3 chart cards (Weekly Earnings, Fleet Status, Repayment Progress).
 */

import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { startOfMonth, subMonths, endOfMonth, subDays, differenceInCalendarDays } from 'date-fns';
import {
    AreaChart, Area, ResponsiveContainer, Tooltip as RTooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
} from 'recharts';
import {
    businessDayEndInstant, businessDayStartInstant, businessToday, formatCurrency,
} from '@/lib/format';
import { fetchPartnerEarliestSignedAt, fetchPartnerWeeklyEarnings } from '@/components/partners/partnerReads';

interface Props {
    partnerId: number;
}

interface DashboardStats {
    vehicle_count: number;
    portfolio_value: number;
    total_repayments: number;
    total_maintenance: number;
    total_payouts: number;
    outstanding: number;
    active_count: number;
    completed_count: number;
    terminated_count: number;
    total_management_fees: number;
    total_partner_earnings: number;
    draft_count: number;
    avg_management_rate: number;
    avg_weekly_repayment: number;
    unassigned_count: number;
}

const FLEET_BAR_COLORS: Record<string, string> = {
    'Contracts': '#0f172a',
    'Procurement': '#94a3b8',
};

const EARNINGS_COLORS = {
    gross: '#94a3b8',    // Slate 400
    earnings: '#0f172a', // Slate 900
};

const tooltipStyle = {
    backgroundColor: 'hsl(var(--card))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
    fontSize: '13px',
};

export default function PartnerSummaryTab({ partnerId }: Props) {
    // Business-calendar months; the RPC bounds are built from their parts, never toISOString().
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    // Fetch earliest contract date for default range: one ordered row (see partnerReads.ts)
    const { data: earliestDate } = useQuery({
        queryKey: ['partner-earliest-contract', partnerId],
        queryFn: () => fetchPartnerEarliestSignedAt(partnerId),
        enabled: !!partnerId,
    });

    const [hasSetDefault, setHasSetDefault] = useState(false);
    useEffect(() => {
        if (earliestDate && !hasSetDefault) {
            setMonthRange({ from: startOfMonth(earliestDate), to: endOfMonth(businessToday()) });
            setHasSetDefault(true);
        }
    }, [earliestDate, hasSetDefault]);

    const fromInstant = businessDayStartInstant(monthRange.from);
    const toInstant = businessDayEndInstant(monthRange.to);

    // Dashboard stats
    const { data: dashStats, isLoading: isLoadingStats } = useQuery({
        queryKey: ['partner-dashboard-stats', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_dashboard_stats', {
                p_partner_id: partnerId,
                p_from_date: fromInstant,
                p_to_date: toInstant,
            });
            if (error) throw error;
            return (data?.[0] ?? {
                vehicle_count: 0, portfolio_value: 0, total_repayments: 0,
                total_maintenance: 0, total_payouts: 0, outstanding: 0,
                active_count: 0, completed_count: 0, terminated_count: 0,
                total_management_fees: 0, total_partner_earnings: 0,
                draft_count: 0, avg_management_rate: 0, avg_weekly_repayment: 0,
                unassigned_count: 0,
            }) as DashboardStats;
        },
        enabled: !!partnerId,
    });

    // Previous period stats for trend comparison — the equally long window of calendar days ending
    // the day before this one opens. Counted in days, not milliseconds: a millisecond offset lands
    // on 23:00 of the day before across a DST boundary, which would move the comparison window by a
    // day for some viewers and not others.
    const periodDays = differenceInCalendarDays(monthRange.to, monthRange.from);
    const prevTo = subDays(monthRange.from, 1);
    const prevFrom = subDays(prevTo, periodDays);
    const prevFromInstant = businessDayStartInstant(prevFrom);
    const prevToInstant = businessDayEndInstant(prevTo);

    const { data: prevStats, isLoading: isLoadingPrev } = useQuery({
        queryKey: ['partner-dashboard-stats-prev', partnerId, prevFromInstant, prevToInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_dashboard_stats', {
                p_partner_id: partnerId,
                p_from_date: prevFromInstant,
                p_to_date: prevToInstant,
            });
            if (error) throw error;
            return (data?.[0] ?? null) as DashboardStats | null;
        },
        enabled: !!partnerId,
    });

    // Weekly earnings trend data: every week in the range, oldest first (see partnerReads.ts)
    const { data: earningsTrend, isLoading: isLoadingEarnings, error: earningsError } = useQuery({
        queryKey: ['partner-earnings-trend', partnerId, fromInstant, toInstant],
        queryFn: () => fetchPartnerWeeklyEarnings(partnerId, fromInstant, toInstant),
        enabled: !!partnerId,
    });

    // Fleet status – use the same RPC as fleet/contracts for exact parity
    const { data: stageCounts, isLoading: isLoadingStages } = useQuery({
        queryKey: ['partner-vehicle-stage-counts', partnerId],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_vehicle_stage_counts', {
                p_partner_id: partnerId,
            });
            if (error) throw error;
            return data as { stage: string; stage_group: string; count: number }[];
        },
        enabled: !!partnerId,
    });

    const earningsTrendData = useMemo(() => {
        if (!earningsTrend) return [];
        return earningsTrend.map(row => ({
            week: 'W' + row.group_key.split('-')[1],
            gross: row.gross_repayments,
            earnings: row.partner_portion,
        }));
    }, [earningsTrend]);

    // ---- Derived values ----
    const totalVehicles = dashStats?.vehicle_count ?? 0;
    const portfolioValue = dashStats?.portfolio_value ?? 0;
    const filteredRepayments = dashStats?.total_repayments ?? 0;
    const outstanding = dashStats?.outstanding ?? 0;
    const repaymentPercentage = portfolioValue > 0 ? (filteredRepayments / portfolioValue) * 100 : 0;

    const pctChange = (current: number, previous: number | undefined | null): number | null => {
        if (previous == null || previous === 0) return null;
        return ((current - previous) / previous) * 100;
    };

    const repaymentsTrend = pctChange(filteredRepayments, prevStats?.total_repayments);
    const outstandingTrend = pctChange(outstanding, prevStats?.outstanding);
    const payoutsTrend = pctChange(dashStats?.total_payouts ?? 0, prevStats?.total_payouts);

    const fleetData = useMemo(() => {
        let contractsTotal = 0;
        let procurementTotal = 0;
        (stageCounts || []).forEach(c => {
            if (c.stage_group === 'contracts') contractsTotal += c.count;
            else if (c.stage_group === 'procurement') procurementTotal += c.count;
        });
        return [
            { name: 'Contracts', value: contractsTotal },
            { name: 'Procurement', value: procurementTotal },
        ];
    }, [stageCounts]);

    const isLoading = isLoadingStats || isLoadingPrev || isLoadingStages || isLoadingEarnings;

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="flex justify-end">
                    <Skeleton className="h-10 w-52" />
                </div>
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} className="h-28 w-full rounded-xl" />
                    ))}
                </div>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <Skeleton className="h-80 w-full rounded-xl" />
                    <Skeleton className="h-80 w-full rounded-xl" />
                    <Skeleton className="h-80 w-full rounded-xl" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Date filter */}
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            {/* 8 Stat Cards — matching fleet-partner order */}
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                <StatCard title="Portfolio Value" value={<CurrencyDisplay amount={portfolioValue} />} description="Total value of all driver contracts" />
                <StatCard title="Repayments" value={<CurrencyDisplay amount={filteredRepayments} />} description="Gross collected from drivers in period" trend={repaymentsTrend} />
                <StatCard title="Partner Earnings" value={<CurrencyDisplay amount={dashStats?.total_partner_earnings ?? 0} />} description={`After ${(dashStats?.avg_management_rate ?? 0).toFixed(2)}% avg management fee`} />
                <StatCard title="Management Fees" value={<CurrencyDisplay amount={dashStats?.total_management_fees ?? 0} />} description="Primelane's fee deducted in period" />

                <StatCard title="Total Payouts" value={<CurrencyDisplay amount={dashStats?.total_payouts ?? 0} />} description="Amount paid to partner account in period" trend={payoutsTrend} />
                <StatCard title="Outstanding" value={<CurrencyDisplay amount={outstanding} />} description="Remaining balance to be recovered" trend={outstandingTrend} trendInverted />
                <StatCard title="Avg Weekly Repayment" value={<CurrencyDisplay amount={dashStats?.avg_weekly_repayment ?? 0} />} description="Average total repayment per week" />
                <StatCard title="Total Vehicles" value={totalVehicles} description="Vehicles in portfolio" />
            </div>

            {/* 3-Column Charts */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Weekly Earnings – Area Chart */}
                <Card className="border border-border/40 flex flex-col">
                    <CardHeader className="pb-2 flex-none">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-medium">
                                Weekly Earnings
                            </CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col gap-4">
                        {earningsError ? (
                            <p className="text-center text-destructive py-8">{(earningsError as Error).message}</p>
                        ) : earningsTrendData.length > 0 ? (
                            <>
                                <div className="relative w-full h-[200px] flex-shrink-0">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={earningsTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.2)" />
                                            <XAxis dataKey="week" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                                            <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                            <RTooltip
                                                formatter={(value: number, name: string) => [formatCurrency(value), name === 'gross' ? 'Gross Repayments' : 'Partner Earnings']}
                                                contentStyle={tooltipStyle}
                                                cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1 }}
                                            />
                                            <Area type="monotone" dataKey="gross" stroke={EARNINGS_COLORS.gross} fill={EARNINGS_COLORS.gross} fillOpacity={0.15} strokeWidth={1.5} dot={false} />
                                            <Area type="monotone" dataKey="earnings" stroke={EARNINGS_COLORS.earnings} fill={EARNINGS_COLORS.earnings} fillOpacity={0.25} strokeWidth={2} dot={false} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Legend */}
                                <div className="w-full space-y-2 mt-auto">
                                    <div className="flex items-center gap-3">
                                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: EARNINGS_COLORS.earnings }} />
                                        <span className="text-sm text-muted-foreground flex-1">Partner Earnings</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: EARNINGS_COLORS.gross }} />
                                        <span className="text-sm text-muted-foreground flex-1">Gross Repayments</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <Skeleton className="h-[220px] w-full" />
                        )}
                    </CardContent>
                </Card>

                {/* Fleet Status – Bar Chart */}
                <Card className="border border-border/40 flex flex-col">
                    <CardHeader className="pb-2 flex-none">
                        <CardTitle className="text-base font-medium">Fleet Status</CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col gap-6">
                        {fleetData.length > 0 ? (
                            <>
                                <div className="relative w-full h-[180px] flex-shrink-0">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={fleetData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.2)" />
                                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                                            <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                                            <RTooltip formatter={(value: number, name: string) => [`${value} vehicles`, name]} contentStyle={tooltipStyle} cursor={{ fill: 'hsl(var(--muted)/0.5)' }} />
                                            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={40}>
                                                {fleetData.map((entry) => (<Cell key={entry.name} fill={FLEET_BAR_COLORS[entry.name] || '#cbd5e1'} />))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="w-full space-y-3 mt-auto">
                                    {fleetData.map((entry) => {
                                        const chartTotal = fleetData.reduce((sum, d) => sum + d.value, 0);
                                        const pct = chartTotal > 0 ? ((entry.value / chartTotal) * 100).toFixed(1) : '0';
                                        return (
                                            <div key={entry.name} className="flex items-center gap-3">
                                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: FLEET_BAR_COLORS[entry.name] || '#cbd5e1' }} />
                                                <span className="text-sm text-muted-foreground flex-1">{entry.name}</span>
                                                <span className="text-sm font-medium tabular-nums">{entry.value}</span>
                                                <span className="text-xs text-muted-foreground w-12 text-right tabular-nums">{pct}%</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <Skeleton className="h-[220px] w-full" />
                        )}
                    </CardContent>
                </Card>

                {/* Repayment Progress – Linear */}
                <Card className="border border-border/40 flex flex-col">
                    <CardHeader className="pb-2 flex-none">
                        <CardTitle className="text-base font-medium">
                            Repayment Progress
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col gap-6 pt-4">
                        <div className="flex-1 flex flex-col justify-center w-full space-y-8">
                            <div className="text-center space-y-2">
                                <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Overall Completion</span>
                                <div className="text-5xl font-bold tracking-tighter text-primary">
                                    {repaymentPercentage.toFixed(2)}%
                                </div>
                            </div>
                            <div className="space-y-2 w-full px-2">
                                <div className="h-4 w-full bg-muted rounded-full overflow-hidden shadow-inner">
                                    <div
                                        className="h-full rounded-full transition-all duration-1000 ease-out border border-border"
                                        style={{
                                            width: `${Math.min(100, Math.max(0, repaymentPercentage))}%`,
                                            backgroundColor: 'hsl(var(--foreground))'
                                        }}
                                    />
                                </div>
                                <div className="flex justify-between text-xs font-medium text-muted-foreground pt-1">
                                    <span>GHS0</span>
                                    <span>{formatCurrency(portfolioValue)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="w-full space-y-3 mt-auto">
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full flex-shrink-0 bg-foreground" />
                                <span className="text-sm text-muted-foreground flex-1">Total Repaid</span>
                                <span className="text-sm font-medium tabular-nums">{formatCurrency(filteredRepayments)}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full flex-shrink-0 bg-orange-500" />
                                <span className="text-sm text-muted-foreground flex-1">Outstanding</span>
                                <span className="text-sm font-medium tabular-nums text-orange-600">{formatCurrency(outstanding)}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

        </div>
    );
}
